import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function RoomCarousel({ images, name }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: images.length > 1,
    align: "start",
    skipSnaps: false,
  });
  const [selected, setSelected] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const multi = images.length > 1;

  return (
    <div className="relative group" data-testid="room-carousel">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {images.map((img, i) => (
            <div
              key={i}
              className="flex-[0_0_100%] min-w-0"
              data-testid={`carousel-slide-${i}`}
            >
              <img
                src={img.src}
                alt={img.alt || name}
                className="w-full h-[380px] md:h-[520px] object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
            </div>
          ))}
        </div>
      </div>

      {multi && (
        <>
          {/* Counter */}
          <div
            className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.25em] text-white bg-black/40 backdrop-blur-sm px-3 py-1.5"
            data-testid="carousel-counter"
          >
            {String(selected + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </div>

          {/* Arrows */}
          <button
            type="button"
            onClick={scrollPrev}
            aria-label="Precedente"
            data-testid="carousel-prev"
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/85 hover:bg-white text-[#1A1C19] backdrop-blur-sm shadow-sm transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            aria-label="Successiva"
            data-testid="carousel-next"
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/85 hover:bg-white text-[#1A1C19] backdrop-blur-sm shadow-sm transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
          >
            <ChevronRight size={20} strokeWidth={1.5} />
          </button>

          {/* Dots */}
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2"
            data-testid="carousel-dots"
          >
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => emblaApi?.scrollTo(i)}
                aria-label={`Vai alla foto ${i + 1}`}
                aria-current={selected === i}
                data-testid={`carousel-dot-${i}`}
                className={`h-1 transition-all duration-300 ${
                  selected === i
                    ? "w-8 bg-white"
                    : "w-4 bg-white/50 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
